import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { getCatalog, preloadCatalogs } from './i18n-catalogs.js';
import { useLocation } from 'react-router-dom';

import { usePlatformBrand } from './use-platform-brand.js';
import { localeFromPathname } from './locale-routing.js';
import type { I18nString, Locale, PlatformBrand } from '@kilat/shared';

/**
 * Sprint 88 — i18n provider for marketing. SEO slice 2b (ADR-0024):
 * **the active locale is derived from the URL**, not localStorage/navigator.
 *
 * Marketing is Kilat-branded so there's no tenant overrides layer; the platform
 * brand's `copy.*` block + the default i18n catalog feed `t()` and
 * `platformBrandCopy()`.
 *
 * Resolution: the leading path segment (`/ms`, `/zh`, `/ta`) selects the locale;
 * the bare path (and any non-locale first segment) is the canonical default
 * (en). There is deliberately **no** `navigator.language` auto-redirect of the
 * bare path — that breaks canonicalisation and is an SEO anti-pattern. The
 * one-tap `<LocaleSwitcher>` navigates to the locale-prefixed URL instead of
 * writing a sticky preference.
 */

export interface I18n {
  readonly locale: Locale;
  readonly supportedLocales: readonly Locale[];
  t(key: string, vars?: Record<string, string | number>): string;
  platformBrandCopy(key: keyof PlatformBrand['copy']): string;
}

function resolveDotted(obj: unknown, key: string): string | undefined {
  return key
    .split('.')
    .reduce<unknown>(
      (acc, part) =>
        acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)
          ? (acc as Record<string, unknown>)[part]
          : undefined,
      obj,
    ) as string | undefined;
}

function interpolate(str: string, vars?: Record<string, string | number>): string {
  if (!vars) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_m, k: string) =>
    k in vars ? String(vars[k]) : `{{${k}}}`,
  );
}

function pickI18n(field: I18nString, locale: Locale): string {
  return field[locale] ?? field.en;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ children }: { readonly children: ReactNode }) {
  const brand = usePlatformBrand();
  const { pathname } = useLocation();
  const locale = localeFromPathname(pathname, brand.supportedLocales, brand.defaultLocale);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  // A locale chosen mid-session may not be in memory yet: load it, then bump
  // this so the strings actually appear. `main.tsx` preloads the locale the URL
  // asks for plus English, so the common path never waits.
  const [loadedTick, setLoadedTick] = useState(0);
  useEffect(() => {
    if (getCatalog(locale)) return;
    let cancelled = false;
    void preloadCatalogs([locale]).then(() => {
      if (!cancelled) setLoadedTick((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const value = useMemo<I18n>(() => {
    // `loadedTick` is read so this recomputes once a late catalogue arrives.
    void loadedTick;
    const catalog = getCatalog(locale) ?? {};
    const fallback = getCatalog('en') ?? {};
    return {
      locale,
      supportedLocales: brand.supportedLocales,
      t(key, vars) {
        const found = resolveDotted(catalog, key) ?? resolveDotted(fallback, key) ?? key;
        return interpolate(found, vars);
      },
      platformBrandCopy(key) {
        return pickI18n(brand.copy[key], locale);
      },
    };
  }, [brand, locale, loadedTick]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(I18nContext);
  if (v) return v;
  return {
    locale: 'en',
    supportedLocales: ['en'],
    t: (key) => key,
    platformBrandCopy: () => '',
  };
}

export const LOCALE_NATIVE_NAMES: Record<Locale, string> = {
  en: 'English',
  ms: 'Bahasa Malaysia',
  zh: '中文',
  ta: 'தமிழ்',
};

/**
 * Read a string from a SPECIFIC locale's catalog, independent of the active
 * locale. The active-locale `t()` above is the right tool 99% of the time; this
 * exists for the one surface that must speak a language OTHER than the page it
 * sits on — the `<LocaleSuggestionBanner>`, whose CTA has to read
 * "Lihat halaman ini dalam Bahasa Malaysia" while the canonical page around it
 * is still rendering in English. Same resolution as `t()`: target locale →
 * English fallback → the key itself, then interpolate.
 */
export function stringInLocale(
  locale: Locale,
  key: string,
  vars?: Record<string, string | number>,
): string {
  // Same registry as the hook: whatever this session has loaded. Callers reach
  // this outside a component, where there is no place to await a chunk, so an
  // unloaded locale falls through to English and then to the key itself rather
  // than blocking.
  const found =
    resolveDotted(getCatalog(locale) ?? {}, key) ??
    resolveDotted(getCatalog('en') ?? {}, key) ??
    key;
  return interpolate(found, vars);
}
